import { X, RefreshCw, Plug } from 'lucide-react';
import { JiraIcon } from './IntegrationIcons';
import type { JiraConnection } from './useJiraConnection';

export function JiraConfigModal({ jira }: { jira: JiraConnection }) {
  const {
    showJiraConfig,
    setShowJiraConfig,
    jiraSiteUrl,
    setJiraSiteUrl,
    jiraEmail,
    setJiraEmail,
    jiraApiToken,
    setJiraApiToken,
    jiraConnected,
    handleJiraDisconnect,
    handleJiraConnect,
    isConnecting,
  } = jira;

  return (
    <>
      {/* Jira Configuration Modal */}
      {showJiraConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--slate-dim)] text-[var(--text-sec)]">
                  <JiraIcon />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Connect to Jira</h2>
                  <p className="text-sm text-[var(--text-ter)]">Enter your Jira Cloud credentials</p>
                </div>
              </div>
              <button onClick={() => setShowJiraConfig(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Jira Site URL <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="url"
                  value={jiraSiteUrl}
                  onChange={(e) => setJiraSiteUrl(e.target.value)}
                  placeholder="https://your-domain.atlassian.net"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Email <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="email"
                  value={jiraEmail}
                  onChange={(e) => setJiraEmail(e.target.value)}
                  placeholder="your-email@company.com"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  API Token <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="password"
                  value={jiraApiToken}
                  onChange={(e) => setJiraApiToken(e.target.value)}
                  placeholder="Your Jira API token"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
                />
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  Create a token at{' '}
                  <a
                    href="https://id.atlassian.com/manage-profile/security/api-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--text-ter)] hover:underline"
                  >
                    Atlassian Account Settings
                  </a>
                </p>
              </div>

              {jiraConnected && (
                <button
                  onClick={handleJiraDisconnect}
                  className="w-full px-4 py-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-sm"
                >
                  Disconnect from Jira
                </button>
              )}
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex justify-end gap-3">
              <button
                onClick={() => setShowJiraConfig(false)}
                className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleJiraConnect}
                disabled={!jiraSiteUrl || !jiraEmail || !jiraApiToken || isConnecting}
                className="px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isConnecting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Plug className="h-4 w-4" />
                )}
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
